import * as yup from 'yup';
import { useForm } from 'react-form-state-manager';
import { useModal } from 'react-modal-hook';
import { useTranslation } from 'react-i18next';

import {
  DuplicateScannerMutation as DuplicateScanner,
  GetScannersForEventQuery as GetScannersForEvent,
} from '__generated__/graphql';

import { getServerErrors } from '../../../common/helpers';
import { useNotifier } from '../../../common/Notifications/NotificationProvider';
import DuplicateScannerMutation from './DuplicateScannerMutation';
import UI from '../../../common/UI';
import useMutation from '../../../api/useMutation';
import useValidation from '../../../common/useValidation';

type Scanner = GetScannersForEvent['project']['event']['scanners'][0];
export type DuplicatedScanner = DuplicateScanner['duplicateScanner'];

const duplicateScannerSchema = yup.object({
  title: yup.string().ensure().required(),
});

export interface DuplicateScannerFormProps {
  scanner: Scanner;
  onSuccess?: (scanner: DuplicatedScanner) => void;
}

export const DuplicateScannerForm = ({ scanner, onSuccess }: DuplicateScannerFormProps) => {
  const { t } = useTranslation();
  const notifier = useNotifier();

  const getValues = (scanner: Scanner) => ({
    title: scanner.title,
  });

  const form = useForm({
    values: getValues(scanner),
  });

  const [
    duplicateScanner, { loading, error },
  ] = useMutation(DuplicateScannerMutation, {
    variables: {
      input: {
        id: scanner.id,
        info: form.values,
      },
    },
    onCompleted: ({ duplicateScanner: scanner }) => {
      onSuccess?.(scanner);
      notifier.success(t('notifications.scanner_duplicated'));
    },
    onError: () => {
      notifier.error(t('something_went_wrong'));
    },
  });

  const { valid, errors } = useValidation({
    schema: duplicateScannerSchema,
    values: {
      scanner: { id: scanner.id },
      ...form.values,
    },
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Form onSubmit={() => duplicateScanner()}>
      <UI.FormGrid>
        <UI.Div>
          <UI.H4 sc={{ mb: 2 }}>
            {t('duplicate_scanner', { scanner: scanner.title })}
          </UI.H4>

          <UI.Paragraph sc={{ mb: 0 }}>
            {t('duplicate_scanner_description')}
          </UI.Paragraph>
        </UI.Div>

        <UI.ServerErrors error={error} />

        <UI.HR />

        <UI.InputGroup sc={{ valid: !errors.title, touched: form.touched.title }}>
          <UI.InputLabel htmlFor="ScannerTitle">
            {t('title')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          <UI.DebouncedInput
            {...form.text('title')}
            maxLength={255}
            placeholder={t('scanner_title_placeholder')}
            autoFocus={!scanner}
            id="ScannerTitle"
          />
          <UI.ErrorMessages attribute={t('title')} errors={errors.title} />
        </UI.InputGroup>

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('duplicate')}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

// eslint-disable-next-line max-len
interface UseDuplicateScannerModalProps extends DuplicateScannerFormProps {}

const useDuplicateScannerModal = ({ scanner, onSuccess }: UseDuplicateScannerModalProps) => {
  const [openModal, closeModal] = useModal(() => {
    const handleSuccess = (scanner: DuplicatedScanner) => {
      closeModal();
      onSuccess?.(scanner);
    };

    return (
      <UI.Modal maxWidth={600} close={closeModal}>
        <DuplicateScannerForm scanner={scanner} onSuccess={handleSuccess} />
      </UI.Modal>
    );
  }, [scanner]);

  return [openModal, closeModal];
};

export default useDuplicateScannerModal;
